// /* eslint-disable react/jsx-no-constructed-context-values */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { api } from "../services/api";

// -----------------------------------------------------------------------
// Helpers de cookie — compartilham sessão com a Landing Page (mesma origem)
// -----------------------------------------------------------------------
const COOKIE_MAX_AGE = 60 * 60 * 24 * 7; // 7 dias

// Detecta se está em produção ou localhost
const isProduction = typeof window !== 'undefined' && window.location.hostname.includes('exercitobrhb.net');
const cookieDomain = isProduction ? '; domain=.exercitobrhb.net' : '';

function setCookie(name: string, value: string) {
  try {
    const encoded = encodeURIComponent(value);
    if (encoded.length > 3900) {
      return;
    }
    document.cookie = `${name}=${encoded}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${cookieDomain}`;
  } catch (error) {
    // Silenciosamente ignorar erros de cookie
  }
}

function removeCookie(name: string) {
  document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${cookieDomain}`;
}

interface AuthProviderProps {
  children: ReactNode;
}

interface Militar {
  id: string;
  nick: string;
  perfil?: string;
  permissoes?: string[];
  max_ordem_permitida?: number;
  patrocinador?: boolean;
  patente?: string;
  id_patente?: string;
}

interface AuthState {
  token: string;
  militar: Militar;
}

interface SignInCredentials {
  nick: string;
  senha: string;
}

interface AuthContextData {
  militar: Militar;
  permissoes: string[];
  signIn(credentials: SignInCredentials): Promise<Error>;
  signOut(): void;
  hasPermission(permissao: string): boolean;
  hasAnyPermission(permissoes: string[]): boolean;
  hasAllPermissions(permissoes: string[]): boolean;
}

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData,
); // Burlar a typagem com o "as AuthContext"

export function AuthProvider({ children }: AuthProviderProps) {
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem("@exbr:token");
    const militar = localStorage.getItem("@exbr:militar");

    if (token && militar) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;

      // Sincroniza cookies caso já esteja logado (sessão anterior ao suporte a cookies)
      setCookie("exbr_token", token);
      setCookie("exbr_militar", militar);

      return { token, militar: JSON.parse(militar) };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(
    async ({ nick, senha }: SignInCredentials): Promise<Error> => {
      try {
        const response = await api.post("sessions", { nick, senha });

        const { token, militar } = response.data;

        if (!militar || !militar.perfil) {
          const authError = new Error(
            "Usuário não autorizado ou credenciais inválidas.",
          );

          return authError;
        }

        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        localStorage.setItem("@exbr:token", token);
        localStorage.setItem("@exbr:militar", JSON.stringify(militar));

        // Cria versão reduzida do militar com apenas dados essenciais para o cookie
        const militarReduzido = {
          id: militar.id,
          nick: militar.nick,
          patente: militar.patente,
          perfil: militar.perfil,
          id_patente: militar.id_patente,
          patrocinador: militar.patrocinador,
        };

        const militarJSON = JSON.stringify(militarReduzido);
        setCookie("exbr_token", token);
        setCookie("exbr_militar", militarJSON);

        setData({ token, militar });

        return null;
      } catch (error) {
        return error;
      }
    },
    [],
  );

  const signOut = useCallback(() => {
    localStorage.removeItem("@exbr:token");
    localStorage.removeItem("@exbr:militar");
    removeCookie("exbr_token");
    removeCookie("exbr_militar");

    setData({} as AuthState);
  }, []);

  // Funções de verificação de permissões
  const permissoes = data.militar?.permissoes || [];

  const hasPermission = useCallback(
    (permissao: string): boolean => {
      return permissoes.includes(permissao);
    },
    [permissoes]
  );

  const hasAnyPermission = useCallback(
    (permissoesRequeridas: string[]): boolean => {
      return permissoesRequeridas.some((p) => permissoes.includes(p));
    },
    [permissoes]
  );

  const hasAllPermissions = useCallback(
    (permissoesRequeridas: string[]): boolean => {
      return permissoesRequeridas.every((p) => permissoes.includes(p));
    },
    [permissoes]
  );

  return (
    <AuthContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        militar: data.militar,
        permissoes,
        signIn,
        signOut,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within a AuthProvider");
  }

  return context;
}
